import { supabase } from '../supabaseClient';

export const chatService = {
  // Send a message
  async sendMessage(senderId, receiverId, content) {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert([{
          sender_id: senderId,
          receiver_id: receiverId,
          content: content,
          created_at: new Date().toISOString(),
          is_read: false
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error sending message:', error);
      return { success: false, error: error.message };
    }
  },

  // Get conversation history between two users
  async getMessages(userId1, userId2) {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .or(`and(sender_id.eq.${userId1},receiver_id.eq.${userId2}),and(sender_id.eq.${userId2},receiver_id.eq.${userId1})`)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  // Get unread count for a user
  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', userId)
        .eq('is_read', false);

      if (error) throw error;
      return { success: true, count: count || 0 };
    } catch (error) {
      return { success: false, error: error.message, count: 0 };
    }
  },

  // Mark messages as read
  async markAsRead(messageIds) {
    if (!messageIds || messageIds.length === 0) return { success: true };
    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({ is_read: true })
        .in('id', messageIds);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return { success: false, error: error.message };
    }
  },

  // Subscribe to presence (Online Users)
  subscribeToPresence(userId, userInfo, onSync) {
    const channel = supabase.channel('online-users', {
      config: {
        presence: {
          key: userId,
        },
      },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const newState = channel.presenceState();
        const users = [];
        // Flatten the presence state
        for (const key in newState) {
            if (newState[key] && newState[key].length > 0) {
                users.push(newState[key][0]); // Take the first session for this user
            }
        }
        onSync(users);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track(userInfo);
        }
      });

    return channel;
  },

  // Subscribe to new messages
  subscribeToMessages(userId, onMessage) {
    const channel = supabase
      .channel('chat-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `receiver_id=eq.${userId}`,
        },
        (payload) => {
          onMessage(payload.new);
        }
      )
   .subscribe();
      
    return channel;
  },

  joinConversation(user1, user2, onTyping, onReaction) {
    // Create a unique channel name for this pair of users
    const channelId = `chat:${[user1, user2].sort().join(':')}`;
    const channel = supabase.channel(channelId);

    channel
      .on('broadcast', { event: 'typing' }, (payload) => {
        if (payload.payload.senderId !== user1) { // If not me
          onTyping(payload.payload);
        }
      })
      .on('broadcast', { event: 'reaction' }, (payload) => {
        if (onReaction) {
          onReaction(payload.payload);
        }
      })
      .subscribe();

    return channel;
  },

  async sendTyping(channel, senderId) {
    if (channel) {
      await channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: { senderId, isTyping: true }
      });
    }
  },

  async sendReaction(channel, messageId, reactions, senderId) {
    if (channel) {
      await channel.send({
        type: 'broadcast',
        event: 'reaction',
        payload: { messageId, reactions, senderId }
      });
    }
    try {
      await supabase
        .from('chat_messages')
        .update({ reactions })
        .eq('id', messageId);
    } catch (err) {
      console.log('Database reactions update notice:', err.message);
    }
  },
  
  async getUnreadBySender(userId) {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id,sender_id')
        .eq('receiver_id', userId)
        .eq('is_read', false);
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }
};
